import { createAsyncThunk } from "@reduxjs/toolkit";
import { NavigateFunction } from "react-router-dom";
import jwt_decode from "jwt-decode";

import API from "Utils/api";
import { RootState } from "../..";
import { setSentryUserFromStorage } from "Utils/sentryUtils";
import { validateRedirectUrl } from "Utils/navigationValidation";
import {
  cognitoSignIn,
  cognitoCompleteNewPassword,
  getCognitoErrorMessage,
  isCognitoError,
  clearAuthTokens,
  AUTH_STORAGE_KEYS,
} from "Utils/cognito";

import {
  // setBackUrl,
  setExpirationDate,
  setLoginErrorText,
  setNewPasswordChallenge,
  setShowGlobalLoader,
} from ".";

type JwtDecode = {
  name: string;
  exp: number;
};

export const getLogin = createAsyncThunk(
  "appState/getLogin",
  async (
    userData: {
      email: string;
      password: string;
      navigateCallBack: NavigateFunction;
    },
    thunkAPI
  ) => {
    const { email, password, navigateCallBack } = userData;
    const state = thunkAPI.getState() as RootState;
    thunkAPI.dispatch(setShowGlobalLoader(true));
    await thunkAPI.dispatch(setLoginErrorText(""));
    try {
      // Step 1: Authenticate with Cognito
      console.info(
        "[Cognito] Email/password login: authenticating with Cognito"
      );
      const signInResult = await cognitoSignIn(email, password);

      // Handle NEW_PASSWORD_REQUIRED challenge (new user invitation)
      if (signInResult.type === "new_password_required") {
        console.info("[Cognito] New password required for invited user");
        thunkAPI.dispatch(setNewPasswordChallenge(signInResult.challenge));
        thunkAPI.dispatch(setShowGlobalLoader(false));
        return;
      }

      const cognitoResult = signInResult.tokens;

      // Step 2: Validate with backend and get Cognito tokens back
      // Backend handles user provisioning via SSODomain
      console.info("[Cognito] Email/password login: validating with backend");
      const response = await API.post("/login/cognito/", {
        id_token: cognitoResult.IdToken,
        access_token: cognitoResult.AccessToken,
        refresh_token: cognitoResult.RefreshToken,
        expires_in: cognitoResult.ExpiresIn,
      });

      // Step 3: Store Cognito tokens (Option B format)
      // Note: Backend uses camelCase due to DRF CamelCase renderer
      const { accessToken, idToken, refreshToken, expiresIn } = response.data;

      localStorage.setItem(AUTH_STORAGE_KEYS.JWT, `Bearer ${accessToken}`);
      localStorage.setItem(AUTH_STORAGE_KEYS.COGNITO_ID_TOKEN, idToken);
      localStorage.setItem(
        AUTH_STORAGE_KEYS.COGNITO_REFRESH_TOKEN,
        refreshToken
      );
      // Store expiry time for refresh logic
      const expiresAt = Date.now() + expiresIn * 1000;
      localStorage.setItem(
        AUTH_STORAGE_KEYS.COGNITO_EXPIRES_AT,
        expiresAt.toString()
      );

      // Set Sentry user context after successful login
      setSentryUserFromStorage();

      console.info("[Cognito] Email/password login complete");
      thunkAPI.dispatch(setShowGlobalLoader(false));
      const backUrl = state?.appState.backUrl;
      navigateCallBack(validateRedirectUrl(backUrl) ? backUrl : "/");
    } catch (error) {
      const baseError = error as BaseError;
      let errorMessage = "There was an error logging in.";

      console.warn("[Cognito] Login error:", error);
      // Check if this is a Cognito error with a code
      if (isCognitoError(error)) {
        errorMessage = getCognitoErrorMessage(error);
      } else if (baseError?.response?.data?.detail) {
        // Backend error
        errorMessage = baseError.response.data.detail;
      } else if (baseError?.response?.status === 401) {
        errorMessage = "email or password is not correct";
      }

      await thunkAPI.dispatch(setLoginErrorText(errorMessage));
      thunkAPI.dispatch(setShowGlobalLoader(false));
    }
  }
);

export const refreshTokenAction = createAsyncThunk(
  "appState/fetchRefreshToken",
  async (navigateCallBack: () => void, thunkAPI) => {
    try {
      const refreshToken = localStorage.getItem(
        AUTH_STORAGE_KEYS.COGNITO_REFRESH_TOKEN
      );
      if (!refreshToken) {
        console.warn("[Cognito] Refresh: no refresh token in storage");
        throw new Error("No refresh token");
      }

      console.info("[Cognito] Refreshing access token");
      const response = await API.post("/login/cognito/refresh/", {
        refresh_token: refreshToken,
      });

      // Note: Backend uses camelCase due to DRF CamelCase renderer
      const { accessToken, expiresIn } = response.data;
      console.info("[Cognito] Token refresh successful");

      localStorage.setItem(AUTH_STORAGE_KEYS.JWT, `Bearer ${accessToken}`);
      const expiresAt = Date.now() + expiresIn * 1000;
      localStorage.setItem(
        AUTH_STORAGE_KEYS.COGNITO_EXPIRES_AT,
        expiresAt.toString()
      );

      await thunkAPI.dispatch(
        setExpirationDate(
          accessToken ? jwt_decode<JwtDecode>(accessToken).exp * 1000 : null
        )
      );
    } catch (err) {
      // Refresh failed - clear all tokens and redirect
      console.warn("[Cognito] Token refresh failed, clearing tokens:", err);
      await thunkAPI.dispatch(setExpirationDate(null));
      clearAuthTokens();
      navigateCallBack();
    }
  }
);

export const fetchCheckAccessToken = createAsyncThunk(
  "appState/fetchCheckAccessToken",
  async (navigateCallBack: () => void, thunkAPI) => {
    const jwt = localStorage.getItem(AUTH_STORAGE_KEYS.JWT);
    const cognitoRefreshToken = localStorage.getItem(
      AUTH_STORAGE_KEYS.COGNITO_REFRESH_TOKEN
    );
    const cognitoExpiresAt = localStorage.getItem(
      AUTH_STORAGE_KEYS.COGNITO_EXPIRES_AT
    );

    // No token at all - redirect to login
    if (!jwt) {
      console.info("[Cognito] No JWT in storage, redirecting to login");
      navigateCallBack();
      return;
    }

    // Check if using Cognito tokens (Option B)
    if (cognitoRefreshToken) {
      // Cognito token flow - check expiration from stored value
      const expiresAt = cognitoExpiresAt ? parseInt(cognitoExpiresAt, 10) : 0;
      const now = Date.now();

      if (expiresAt && expiresAt > now) {
        // Token still valid
        console.info(
          "[Cognito] Access token still valid, expires in %ds",
          Math.round((expiresAt - now) / 1000)
        );
        await thunkAPI.dispatch(setExpirationDate(expiresAt));
        return;
      }

      // Token expired - try to refresh
      console.info("[Cognito] Access token expired, attempting refresh");
      try {
        const response = await API.post("/login/cognito/refresh/", {
          refresh_token: cognitoRefreshToken,
        });
        // Note: Backend uses camelCase due to DRF CamelCase renderer
        const { accessToken, expiresIn } = response.data;
        const newExpiresAt = Date.now() + expiresIn * 1000;

        localStorage.setItem(AUTH_STORAGE_KEYS.JWT, `Bearer ${accessToken}`);
        localStorage.setItem(
          AUTH_STORAGE_KEYS.COGNITO_EXPIRES_AT,
          newExpiresAt.toString()
        );
        await thunkAPI.dispatch(setExpirationDate(newExpiresAt));
      } catch (err) {
        // Refresh failed - clear tokens and redirect
        console.warn(
          "[Cognito] Token check refresh failed, clearing tokens:",
          err
        );
        clearAuthTokens();
        navigateCallBack();
      }
      return;
    }

    // Legacy Django JWT flow (fallback for existing sessions)
    try {
      const currentAccessToken = jwt.replace("Bearer ", "");
      await API.post("/login/verify/", {
        token: currentAccessToken,
      });
      await thunkAPI.dispatch(
        setExpirationDate(
          currentAccessToken
            ? jwt_decode<JwtDecode>(currentAccessToken).exp * 1000
            : null
        )
      );
    } catch (err) {
      try {
        localStorage.removeItem(AUTH_STORAGE_KEYS.JWT);
        const currentRefreshToken = (
          localStorage.getItem(AUTH_STORAGE_KEYS.JWT_REFRESH) as string
        ).replace("Bearer ", "");
        const response = await API.post("/login/refresh/", {
          refresh: currentRefreshToken,
        });
        localStorage.setItem(
          AUTH_STORAGE_KEYS.JWT,
          `Bearer ${response.data.access}`
        );
        await thunkAPI.dispatch(
          setExpirationDate(
            response.data.access
              ? jwt_decode<JwtDecode>(response.data.access).exp * 1000
              : null
          )
        );
      } catch (refreshErr) {
        navigateCallBack();
      }
    }
  }
);

export const completeNewPassword = createAsyncThunk(
  "appState/completeNewPassword",
  async (
    userData: {
      session: string;
      email: string;
      newPassword: string;
      navigateCallBack: NavigateFunction;
    },
    thunkAPI
  ) => {
    const { session, email, newPassword, navigateCallBack } = userData;
    const state = thunkAPI.getState() as RootState;
    thunkAPI.dispatch(setShowGlobalLoader(true));
    await thunkAPI.dispatch(setLoginErrorText(""));

    try {
      // Complete the NEW_PASSWORD_REQUIRED challenge
      const cognitoResult = await cognitoCompleteNewPassword(
        session,
        email,
        newPassword
      );

      // Validate with backend
      console.info("[Cognito] New password set, validating with backend");
      const response = await API.post("/login/cognito/", {
        id_token: cognitoResult.IdToken,
        access_token: cognitoResult.AccessToken,
        refresh_token: cognitoResult.RefreshToken,
        expires_in: cognitoResult.ExpiresIn,
      });

      // Store tokens
      const { accessToken, idToken, refreshToken, expiresIn } = response.data;

      localStorage.setItem(AUTH_STORAGE_KEYS.JWT, `Bearer ${accessToken}`);
      localStorage.setItem(AUTH_STORAGE_KEYS.COGNITO_ID_TOKEN, idToken);
      localStorage.setItem(
        AUTH_STORAGE_KEYS.COGNITO_REFRESH_TOKEN,
        refreshToken
      );
      const expiresAt = Date.now() + expiresIn * 1000;
      localStorage.setItem(
        AUTH_STORAGE_KEYS.COGNITO_EXPIRES_AT,
        expiresAt.toString()
      );

      setSentryUserFromStorage();

      // Clear the challenge state
      thunkAPI.dispatch(setNewPasswordChallenge(null));
      thunkAPI.dispatch(setShowGlobalLoader(false));

      const backUrl = state?.appState.backUrl;
      navigateCallBack(validateRedirectUrl(backUrl) ? backUrl : "/");
    } catch (error) {
      const baseError = error as BaseError;
      let errorMessage = "Failed to set new password.";

      if (isCognitoError(error)) {
        errorMessage = getCognitoErrorMessage(error);
      } else if (baseError?.response?.data?.detail) {
        errorMessage = baseError.response.data.detail;
      }

      await thunkAPI.dispatch(setLoginErrorText(errorMessage));
      thunkAPI.dispatch(setShowGlobalLoader(false));
    }
  }
);
